import { FontFamily, Text, TextColor, TextSize, TextVariant } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import { StatusBadge } from './status-badge';
import type { HeroScoringSection } from './types';

interface HeroScoringScoreSectionProps {
  section: HeroScoringSection;
}

const BAR_GRADIENT_BY_LIKERT: Record<'low' | 'medium' | 'high', string> = {
  low: 'bg-gradient-to-r from-error-200 to-error-500',
  medium: 'bg-gradient-to-r from-yellow-200 to-yellow-400',
  high: 'bg-gradient-to-r from-success-200 to-success-400',
};

function HeroScoringScoreSection({ section }: HeroScoringScoreSectionProps) {
  if (section.body.kind !== 'score') return null;

  const { title, description, body } = section;
  const { score } = body;
  const likert = body.status ?? 'unscored';

  const isScored = score !== null && score !== undefined;
  // score-threshold-allow: clamps to the 0-100 axis, not a band
  const clampedScore = isScored ? Math.max(0, Math.min(100, score)) : 0;
  const gradientClass = likert !== 'unscored' ? BAR_GRADIENT_BY_LIKERT[likert] : 'bg-gray-utility-300';

  return (
    <div className="flex flex-col gap-3 px-8 py-6">
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text fontFamily={FontFamily.Serif} size={TextSize.Xl} elementType="span" showEllipsis={false}>
            {title}
          </Text>
          {description !== undefined && (
            <Text variant={TextVariant.XsRegular} color={TextColor.Quaternary} showEllipsis={false} elementType="p">
              {description}
            </Text>
          )}
        </div>
        {likert !== 'unscored' && <StatusBadge status={likert} />}
      </div>
      <div
        className="h-2 w-full overflow-hidden bg-gray-utility-100"
        role="meter"
        aria-label={`${title} score`}
        aria-valuemin={0}
        aria-valuemax={100 /* score-threshold-allow: meter axis maximum */}
        aria-valuenow={isScored ? clampedScore : undefined}
      >
        <div
          data-testid={`score-bar-fill-${likert}`}
          className={cx('h-full transition-all', gradientClass)}
          style={{ width: `${clampedScore}%` }}
        />
      </div>
    </div>
  );
}

export { HeroScoringScoreSection };
export type { HeroScoringScoreSectionProps };
